import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel
} from "@/components/ui/dropdown-menu";
import { Input } from '@/components/ui/input';
import { toast } from '@/components/ui/use-toast';
import { getBusinessOpportunities, getHubspotOpportunities, getHubspotLineItemsByDealId } from '@/services/business-opportunities';
import { BusinessOpportunity, HubspotOpportunity, HubspotLineItem, OpportunityStatus } from '@/types/business-opportunities';
import { useAuthContext } from '@/contexts/AuthContext';
import { formatCurrency } from '@/lib/utils';
import { 
  Briefcase, ChevronDown, Edit, Eye, MoreHorizontal, Plus, Trash2, 
  FileText, Building, ExternalLink, BarChart4, Filter, Grid, List, Search, Calendar,
  ArrowDown, ArrowUp, ArrowUpDown, RefreshCw, TrendingUp, Target, Star
} from 'lucide-react';
import { mockOpportunities, mockCustomers } from '@/mockData/businessOpportunities';
import { supabase } from '@/lib/supabase';
import { initializeHubspotTables } from '@/utils/initialize-hubspot-data';
import { createSampleHubspotData } from '@/utils/initialize-hubspot-data';
import { Switch } from '@/components/ui/switch';
import { Helmet } from 'react-helmet-async';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from "@/components/ui/select";
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';


type OpportunityWithCustomer = BusinessOpportunity & {
  customer: {
    id: string;
    name: string;
    logo_url?: string;
  };
  owner_name?: string;
};

// Extended type for HubSpot opportunities with customer data
type HubspotOpportunityWithCustomer = Omit<HubspotOpportunity, 'customer_name'> & {
  customer?: {
    id: string;
    name: string;
    logo_url?: string;
  };
  status: OpportunityStatus;
  customer_name?: string;
  owner_name?: string;
};

// Union type for displaying either type of opportunity
type DisplayOpportunity = OpportunityWithCustomer | HubspotOpportunityWithCustomer;

// For type safety, implement a function to cast HubSpot data to the correct DisplayOpportunity type
const mapHubspotDataToDisplayFormat = (data: any[]): HubspotOpportunityWithCustomer[] => {
  if (!data || !Array.isArray(data)) return [];
  
  return data.map(item => ({
    ...item,
    // Use the status field directly from the schema
    status: item.status || 'open', // Use status field, default to 'open' if missing
    customer: item.customer || {
      id: item.company_id || '',
      name: 'Unknown Customer',
    },
    owner_name: item.owner_name || 'Não atribuído'
  }));
};

// Type for sorting columns
type SortColumn = 'opportunity' | 'owner' | 'value' | 'probability' | 'status' | 'created_at';
type SortDirection = 'asc' | 'desc' | null;

const BusinessOpportunitiesPage: React.FC = () => {
  const [opportunities, setOpportunities] = useState<OpportunityWithCustomer[]>([]);
  const [hubspotOpportunities, setHubspotOpportunities] = useState<HubspotOpportunityWithCustomer[]>([]);
  const [filteredOpportunities, setFilteredOpportunities] = useState<DisplayOpportunity[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('open');
  const [sortOrder, setSortOrder] = useState<'newest'|'oldest'>('newest');
  const [isLoading, setIsLoading] = useState(true);
  const [loadingError, setLoadingError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [showHubspotData, setShowHubspotData] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  // New state variables
  const [viewMode, setViewMode] = useState<'table'>('table');
  const [timeFilter, setTimeFilter] = useState<'all' | 'week' | 'month' | 'quarter'>('all');
  const [valueFilter, setValueFilter] = useState<'all' | 'high' | 'medium' | 'low'>('all');
  const [showFilters, setShowFilters] = useState(false);
  const [activeTab, setActiveTab] = useState<'all' | 'hubspot' | 'internal'>('hubspot');
  const [vendorFilter, setVendorFilter] = useState('');
  
  // Estatísticas para os cards
  const [stats, setStats] = useState({
    totalOpportunities: 0,
    totalValue: 0,
    conversionRate: 0,
    openCount: 0,
    negotiationCount: 0,
    wonCount: 0,
    lostCount: 0
  });
  
  // New state variables for sorting
  const [sortColumn, setSortColumn] = useState<SortColumn | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);
  
  const navigate = useNavigate();
  const { user } = useAuthContext();

  const fetchOpportunities = async () => {
    try {
      setIsLoading(true);
      setLoadingError(null);
      
      // Initialize Hubspot tables if they don't exist
      await initializeHubspotTables();
      
      // Add retry mechanism for Hubspot data
      let hubspotData: HubspotOpportunityWithCustomer[] = [];
      let hubspotError = null;
      
      // Try up to 3 times with increasing timeout
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          console.log(`Attempting to fetch Hubspot opportunities (attempt ${attempt + 1}/3)`);
          hubspotData = await getHubspotOpportunities();
          hubspotError = null;
          // If successful, break out of the retry loop
          break;
        } catch (error) {
          hubspotError = error;
          console.error(`Attempt ${attempt + 1} failed:`, error);
          
          if (attempt < 2) {
            // Wait before retrying (increasing delay: 1s, 2s)
            const delay = (attempt + 1) * 1000;
            console.log(`Retrying in ${delay}ms...`);
            await new Promise(resolve => setTimeout(resolve, delay));
          }
        }
      }
      
      // Log the final result of Hubspot data fetching
      if (hubspotError) {
        console.error('All attempts to fetch Hubspot opportunities failed');
      } else {
        console.log('Fetched Hubspot opportunities:', hubspotData);
      }
      
      // Always fetch regular opportunities
      let regularData: OpportunityWithCustomer[] = [];
      try {
        const fetchedData = await getBusinessOpportunities("");
        regularData = fetchedData as OpportunityWithCustomer[];
        console.log('Fetched regular opportunities:', regularData);
      } catch (regularError) {
        console.error('Error fetching regular opportunities:', regularError);
        if (!hubspotData || hubspotData.length === 0) {
          setLoadingError('Não foi possível carregar as oportunidades. Tente novamente.');
          toast({
            title: 'Erro ao carregar oportunidades internas',
            description: 'Não foi possível carregar as oportunidades de negócio internas.',
            variant: 'destructive',
          });
        }
      }
      
      // Set data in state
      const mappedHubspotData = mapHubspotDataToDisplayFormat(hubspotData);
      setHubspotOpportunities(mappedHubspotData);
      setOpportunities(regularData as OpportunityWithCustomer[] || []);
      
      // Decide which data to show initially
      if (mappedHubspotData && mappedHubspotData.length > 0) {
        setFilteredOpportunities(mappedHubspotData);
        setShowHubspotData(true);
      } else {
        if (!regularData || regularData.length === 0) {
          console.log('No opportunities found in database, setting up mock data');
          await setupMockData();
          const refreshedData = await getBusinessOpportunities("");
          setOpportunities(refreshedData as OpportunityWithCustomer[]);
          setFilteredOpportunities(refreshedData as DisplayOpportunity[]);
        } else {
          setFilteredOpportunities(regularData as DisplayOpportunity[]);
        }
        setShowHubspotData(false);
      }
    } catch (error) {
      console.error('Error in overall opportunity fetching process:', error);
      setLoadingError('Encontramos um problema ao carregar as oportunidades. Tente novamente.');
      toast({
        title: 'Erro ao carregar dados',
        description: 'Encontramos um problema ao carregar as oportunidades. Tente novamente mais tarde.',
        variant: 'destructive',
      });
      // Fall back to regular opportunities if available
      if (opportunities.length > 0) {
        setFilteredOpportunities(opportunities as DisplayOpportunity[]);
        setShowHubspotData(false);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Trigger fetch when component mounts or retry count changes
  useEffect(() => {
    fetchOpportunities();
  }, [retryCount]);
  
  // Function to manually retry loading data
  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
  };

  // Setup mock data function
  const setupMockData = async () => {
    try {
      console.log('Setting up mock data');
      
      // Insert mock customers if they don't exist
      for (const customer of mockCustomers) {
        try {
          const { data: existingCustomer } = await supabase
            .from('customers')
            .select('id')
            .eq('id', customer.id)
            .single();
            
          if (!existingCustomer) {
            // Create a clean customer object without unnecessary fields
            const { error: insertError } = await supabase
              .from('customers')
              .insert(customer);
            
            if (insertError) {
              console.error('Error inserting customer:', insertError);
            } else {
              console.log('Inserted customer:', customer.name);
            }
          }
        } catch (customerError) {
          // Ignore PGRST116 errors (no rows returned)
          if (customerError.code !== 'PGRST116') {
            console.error('Error processing customer:', customerError);
          }
        }
      }
      
      // Insert mock opportunities if they don't exist
      for (const opportunity of mockOpportunities) {
        try {
          const { data: existingOpp } = await supabase
            .from('business_opportunities')
            .select('id')
            .eq('id', opportunity.id)
            .single();
            
          if (!existingOpp) {
            // Create a clean opportunity object without organization_id
            const cleanOpportunity = { ...opportunity };
            if ('organization_id' in cleanOpportunity) {
              // Use type assertion to remove the property
              delete (cleanOpportunity as any).organization_id;
            }
            
            const { error: insertError } = await supabase
              .from('business_opportunities')
              .insert(cleanOpportunity);
            
            if (insertError) {
              console.error('Error inserting opportunity:', insertError);
            } else {
              console.log('Inserted opportunity:', cleanOpportunity.name);
            }
          }
        } catch (oppError) {
          // Ignore PGRST116 errors (no rows returned)
          if (oppError.code !== 'PGRST116') {
            console.error('Error processing opportunity:', oppError);
          }
        }
      }
      
      console.log('Mock data setup complete');
    } catch (error) {
      console.error('Error setting up mock data:', error);
    }
  };

  // Function to handle sorting
  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      // Toggle direction: asc -> desc -> null
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        setSortColumn(null);
        setSortDirection(null);
      } else {
        setSortDirection('asc');
      }
    } else {
      // New column, start with ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Renderize a table header with sorting
  const renderSortableHeader = (column: SortColumn, label: string) => (
    <TableHead 
      className="cursor-pointer select-none"
      onClick={() => handleSort(column)}
    >
      <div className="flex items-center">
        {label}
        <span className="ml-1">
          {sortColumn === column ? (
            sortDirection === 'asc' ? (
              <ArrowUp className="h-4 w-4" />
            ) : (
              <ArrowDown className="h-4 w-4" />
            )
          ) : (
            <ArrowUpDown className="h-4 w-4 opacity-30" />
          )}
        </span>
      </div>
    </TableHead>
  );

  // Filter opportunities based on all criteria
  useEffect(() => {
    let result: DisplayOpportunity[] = [];
    
    // Aplicar apenas aos dados do HubSpot
    result = [...hubspotOpportunities];
      
    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter((opp) => {
        if (isHubspotOpportunity(opp)) {
          return opp.dealname.toLowerCase().includes(query) || 
                (opp.customer_name && opp.customer_name.toLowerCase().includes(query));
        } else {
          return opp.name.toLowerCase().includes(query) || 
                 opp.customer.name.toLowerCase().includes(query);
        }
      });
    }
    
    // Apply vendor filter
    if (vendorFilter) {
      const query = vendorFilter.toLowerCase();
      result = result.filter((opp) => {
        const ownerName = isHubspotOpportunity(opp) ? 
          (opp.owner_name || '') : (opp.owner_name || '');
        return ownerName.toLowerCase().includes(query);
      });
    }
    
    // Apply status filter
    if (statusFilter !== 'all') {
      result = result.filter((opp) => opp.status === statusFilter);
    }
    
    // Apply time filter
    if (timeFilter !== 'all') {
      const now = new Date();
      const getDateLimit = () => {
        switch (timeFilter) {
          case 'week':
            const weekAgo = new Date();
            weekAgo.setDate(now.getDate() - 7);
            return weekAgo;
          case 'month':
            const monthAgo = new Date();
            monthAgo.setMonth(now.getMonth() - 1);
            return monthAgo;
          case 'quarter':
            const quarterAgo = new Date();
            quarterAgo.setMonth(now.getMonth() - 3);
            return quarterAgo;
          default:
            return new Date(0);
        }
      };
      
      const dateLimit = getDateLimit();
      
      result = result.filter((opp) => {
        const date = isHubspotOpportunity(opp) ? 
          (opp.createdate ? new Date(opp.createdate) : new Date()) : 
          (opp.created_at ? new Date(opp.created_at) : new Date());
        return date >= dateLimit;
      });
    }
    
    // Apply value filter
    if (valueFilter !== 'all') {
      result = result.filter((opp) => {
        const value = isHubspotOpportunity(opp) ? (opp.amount || 0) : opp.total_value;
        
        switch (valueFilter) {
          case 'high':
            return value >= 100000;
          case 'medium':
            return value >= 10000 && value < 100000;
          case 'low':
            return value < 10000;
          default:
            return true;
        }
      });
    }
    
    // Apply sorting if column is selected
    if (sortColumn && sortDirection) {
      result.sort((a, b) => {
        let valueA: any;
        let valueB: any;
        
        // Extract values based on the sort column
        switch (sortColumn) {
          case 'opportunity':
            valueA = isHubspotOpportunity(a) ? a.dealname : a.name;
            valueB = isHubspotOpportunity(b) ? b.dealname : b.name;
            break;
          case 'owner':
            valueA = isHubspotOpportunity(a) ? (a.owner_name || '') : (a.owner_name || '');
            valueB = isHubspotOpportunity(b) ? (b.owner_name || '') : (b.owner_name || '');
            break;
          case 'value':
            valueA = isHubspotOpportunity(a) ? (a.amount || 0) : a.total_value;
            valueB = isHubspotOpportunity(b) ? (b.amount || 0) : b.total_value;
            break;
          case 'probability':
            valueA = isHubspotOpportunity(a) ? (a.hs_deal_stage_probability || 0) : (a.probability || 0);
            valueB = isHubspotOpportunity(b) ? (b.hs_deal_stage_probability || 0) : (b.probability || 0);
            break;
          case 'status':
            valueA = a.status;
            valueB = b.status;
            break;
          case 'created_at':
            valueA = isHubspotOpportunity(a) ? a.createdate : a.created_at;
            valueB = isHubspotOpportunity(b) ? b.createdate : b.created_at;
            valueA = valueA ? new Date(valueA).getTime() : 0;
            valueB = valueB ? new Date(valueB).getTime() : 0;
            break;
          default:
            valueA = 0;
            valueB = 0;
        }
        
        // String comparison for string values
        if (typeof valueA === 'string' && typeof valueB === 'string') {
          const comparison = valueA.localeCompare(valueB);
          return sortDirection === 'asc' ? comparison : -comparison;
        }
        
        // Numeric comparison
        const comparison = valueA - valueB;
        return sortDirection === 'asc' ? comparison : -comparison;
      });
    } else {
      // Default sort by date if no sort column specified
    result.sort((a, b) => {
      const dateA = isHubspotOpportunity(a) ? a.createdate : a.created_at;
      const dateB = isHubspotOpportunity(b) ? b.createdate : b.created_at;
      
      if (sortOrder === 'newest') {
        return dateB ? new Date(dateB).getTime() : 0 - (dateA ? new Date(dateA).getTime() : 0);
      } else {
        return dateA ? new Date(dateA).getTime() : 0 - (dateB ? new Date(dateB).getTime() : 0);
      }
    });
    }
    
    setFilteredOpportunities(result);
    
    // Calcular estatísticas para os cards
    calculateStats(result);
  }, [hubspotOpportunities, searchQuery, statusFilter, sortOrder, timeFilter, valueFilter, vendorFilter, sortColumn, sortDirection]);
  
  // Função para calcular estatísticas
  const calculateStats = (opportunities: DisplayOpportunity[]) => {
    const totalOpps = opportunities.length;
    let totalValue = 0;
    let openCount = 0;
    let negotiationCount = 0;
    let wonCount = 0;
    let lostCount = 0;
    
    opportunities.forEach(opp => {
      // Calcular valor total
      if (isHubspotOpportunity(opp)) {
        totalValue += opp.amount || 0;
      } else {
        totalValue += opp.total_value;
      }
      
      // Contar por status
      switch(opp.status) {
        case 'open':
          openCount++;
          break;
        case 'negotiation':
          negotiationCount++;
          break;
        case 'won':
          wonCount++;
          break;
        case 'lost':
          lostCount++;
          break;
      }
    });
    
    // Calcular taxa de conversão (oportunidades ganhas / total de oportunidades fechadas)
    const closedOpps = wonCount + lostCount;
    const conversionRate = closedOpps > 0 ? (wonCount / closedOpps) * 100 : 0;
    
    setStats({
      totalOpportunities: totalOpps,
      totalValue: totalValue,
      conversionRate: conversionRate,
      openCount,
      negotiationCount,
      wonCount,
      lostCount
    });
  };

  // Get current items for pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredOpportunities.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredOpportunities.length / itemsPerPage);

  // Change page
  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  // Pagination component
  const renderPagination = () => {
    if (totalPages <= 1) return null;

    // Calculate which pages to show (show up to 7 pages, with ellipsis if needed)
    let pagesToShow: (number | string)[] = [];
    
    if (totalPages <= 10) {
      // If 10 or fewer pages, show all
      pagesToShow = Array.from({ length: totalPages }, (_, i) => i + 1);
    } else {
      // Always show first page
      pagesToShow.push(1);
      
      // Calculate start and end of current window
      let startPage = Math.max(2, currentPage - 2);
      let endPage = Math.min(totalPages - 1, currentPage + 2);
      
      // Adjust window to show 5 pages when possible
      if (currentPage <= 4) {
        endPage = Math.min(6, totalPages - 1);
      } else if (currentPage >= totalPages - 3) {
        startPage = Math.max(totalPages - 5, 2);
      }
      
      // Add ellipsis if needed before startPage
      if (startPage > 2) {
        pagesToShow.push('...');
      }
      
      // Add pages in current window
      for (let i = startPage; i <= endPage; i++) {
        pagesToShow.push(i);
      }
      
      // Add ellipsis if needed after endPage
      if (endPage < totalPages - 1) {
        pagesToShow.push('...');
      }
      
      // Always show last page
      pagesToShow.push(totalPages);
    }

    return (
      <div className="flex flex-col sm:flex-row items-center justify-between mt-6 gap-4">
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-500">Itens por página:</span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                {itemsPerPage}
                <ChevronDown className="ml-2 h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={() => {
                setItemsPerPage(5);
                setCurrentPage(1);
              }}>5</DropdownMenuItem>
              <DropdownMenuItem onClick={() => {
                setItemsPerPage(10);
                setCurrentPage(1);
              }}>10</DropdownMenuItem>
              <DropdownMenuItem onClick={() => {
                setItemsPerPage(20);
                setCurrentPage(1);
              }}>20</DropdownMenuItem>
              <DropdownMenuItem onClick={() => {
                setItemsPerPage(50);
                setCurrentPage(1);
              }}>50</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        
        <div className="flex flex-wrap justify-center items-center gap-1">
          <Button 
            variant="outline" 
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            size="sm"
            className="px-3"
          >
            Anterior
          </Button>
          
          {pagesToShow.map((page, index) => {
            if (page === '...') {
              return (
                <span key={`ellipsis-${index}`} className="px-2 text-gray-500">
                  ...
                </span>
              );
            }
            
            return (
              <Button
                key={`page-${page}`}
                variant={currentPage === page ? "default" : "outline"}
                onClick={() => handlePageChange(page as number)}
                size="sm"
                className="min-w-[36px] px-2"
              >
                {page}
              </Button>
            );
          })}
          
          <Button 
            variant="outline" 
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            size="sm"
            className="px-3"
          >
            Próxima
          </Button>
        </div>
        
        <div className="text-sm text-gray-500">
          Exibindo {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, filteredOpportunities.length)} de {filteredOpportunities.length}
        </div>
      </div>
    );
  };

  const handleCreateNew = () => {
    navigate('/business-opportunities/new');
  };

  const handleViewDetails = (id: string) => {
    navigate(`/business-opportunities/${id}`);
  };

  const handleEdit = (id: string) => {
    navigate(`/business-opportunities/${id}/edit`);
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'open':
        return 'bg-blue-100 text-blue-800';
      case 'won':
        return 'bg-green-100 text-green-800';
      case 'lost':
        return 'bg-red-100 text-red-800';
      case 'canceled':
        return 'bg-gray-100 text-gray-800';
      case 'negotiation':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const statusLabels: Record<OpportunityStatus, string> = {
    open: 'Aberta',
    negotiation: 'Em Negociação',
    won: 'Ganha',
    lost: 'Perdida',
    canceled: 'Cancelada'
  };

  // Get all unique deal stages for the filter dropdown
  const dealStages = showHubspotData && hubspotOpportunities.length > 0
    ? [...new Set(hubspotOpportunities.map(opp => opp.status))]
    : [];

  // Ações da tabela
  const renderActions = (id: string, isHubspotOpp: boolean = false) => (
    <div className="flex justify-end space-x-1">
      {isHubspotOpp && (
      <Button
        variant="ghost"
        size="icon"
        onClick={(e) => {
          e.stopPropagation();
            window.open(`https://app.hubspot.com/contacts/21779418/record/0-3/${id}`, '_blank');
        }}
        className="h-8 w-8"
          title="Ver no Hubspot"
      >
          <ExternalLink className="h-4 w-4" />
      </Button>
      )}
    </div>
  );

  // Type guard to check if an opportunity is a HubSpot opportunity
  const isHubspotOpportunity = (opp: any): opp is HubspotOpportunityWithCustomer => {
    // Verificar propriedades específicas do HubspotOpportunity
    return 'dealname' in opp && 'company_id' in opp;
  }

  // Render probability bar component
  const renderProbabilityBar = (probability: number | undefined) => {
    if (probability === undefined) return '-';
    
    // Convert decimal probability to percentage if less than 1
    let probValue = probability;
    if (probability < 1) {
      probValue = probability * 100;
    }
    
    // Ensure value is within 0-100 range
    probValue = Math.min(Math.max(0, probValue), 100);
    
    return (
      <div className="flex flex-col space-y-1">
        <div className="font-extrabold">{Math.round(probValue)}%</div>
        <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden">
          <div 
            className={`h-full ${getProbabilityBarColor(probValue)}`} 
            style={{ width: `${probValue}%` }}
          ></div>
        </div>
      </div>
    );
  };
  
  // Get probability bar color based on value
  const getProbabilityBarColor = (probability: number) => {
    if (probability < 30) return 'bg-red-500';
    if (probability < 70) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  // Renderiza a tabela de oportunidades
  const renderTable = () => {
    if (isLoading) {
      return (
        <div className="flex flex-col justify-center items-center py-16 space-y-4">
          <div className="w-12 h-12 rounded-full border-4 border-blue-500 border-t-transparent animate-spin"></div>
          <span className="text-xl font-extrabold">Carregando oportunidades...</span>
          <p className="text-gray-500 text-sm">Isso pode levar até 10 segundos</p>
        </div>
      );
    }

    if (loadingError && filteredOpportunities.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center space-y-6 py-16">
          <div className="bg-red-50 border border-red-200 text-red-800 p-6 rounded-lg max-w-lg mx-auto text-center shadow-sm">
            <h3 className="text-xl font-extrabold mb-3">Erro de conexão</h3>
            <p className="mb-4">{loadingError}</p>
            <p className="text-sm text-red-600 mb-4">
              Possíveis problemas: conexão de rede instável ou servidor indisponível.
            </p>
            <Button onClick={handleRetry} className="bg-red-600 hover:bg-red-700 text-white">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Tentar novamente
            </Button>
          </div>
        </div>
      );
    }

    if (filteredOpportunities.length === 0) {
      return (
        <div className="text-center py-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
            <Briefcase className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-extrabold mb-2">Nenhuma oportunidade encontrada</h3>
          <p className="text-gray-500 mb-4">Nenhuma oportunidade corresponde aos critérios de filtro atuais.</p>
          <Button onClick={handleCreateNew}>
            <Plus className="w-4 h-4 mr-2" />
            Criar Nova Oportunidade
          </Button>
        </div>
      );
    }

    return (
      <Table>
        <TableHeader>
          <TableRow>
            {renderSortableHeader('opportunity', 'Oportunidade')}
            {renderSortableHeader('owner', 'Vendedor')}
            {renderSortableHeader('value', 'Valor')}
            {renderSortableHeader('probability', 'Probabilidade')}
            {renderSortableHeader('status', 'Status')}
            {renderSortableHeader('created_at', 'Criada em')}
            <TableHead className="text-right">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {currentItems.map((opp) => {
            const isHubspot = isHubspotOpportunity(opp);
            const title = isHubspot ? opp.dealname : opp.name;
            const customerName = isHubspot ? (opp.customer_name || "Cliente não vinculado") : opp.customer.name;
            const value = isHubspot ? (opp.amount || 0) : opp.total_value;
            const currency = isHubspot ? (opp.deal_currency_code || 'BRL') : opp.currency_code;
            const createDate = isHubspot ? opp.createdate : opp.created_at;
            const probability = isHubspot ? (opp.hs_deal_stage_probability || 0) : (opp.probability || 0);
            const ownerName = isHubspot ? (opp.owner_name || 'Não atribuído') : (opp.owner_name || 'Não atribuído');
            
            return (
              <TableRow 
                key={opp.id}
                className="cursor-pointer hover:bg-gray-50"
                onClick={() => handleViewDetails(opp.id)}
              >
                <TableCell>
                  <div>
                    <div className="font-extrabold">{title}</div>
                    <div className="text-sm text-gray-500">
                      {customerName}
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="font-extrabold">{ownerName}</div>
                </TableCell>
                <TableCell className="text-right">
                  <div className="font-extrabold">
                    {formatCurrency(value, currency)}
                  </div>
                  {isHubspot && opp.hs_mrr && (
                    <div className="text-xs text-gray-500">
                      MRR: {formatCurrency(opp.hs_mrr, currency)}
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  {renderProbabilityBar(probability)}
                </TableCell>
                <TableCell>
                  <div className={`px-2 py-1 rounded text-xs font-extrabold inline-block ${getStatusBadgeClass(opp.status)}`}>
                    {statusLabels[opp.status as OpportunityStatus] || opp.status}
                  </div>
                </TableCell>
                <TableCell>
                  {createDate ? new Date(createDate).toLocaleDateString() : '-'}
                </TableCell>
                <TableCell className="text-right">
                  {renderActions(opp.id, isHubspot)}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    );
  };

  // Render the main content with a responsive layout
  return (
    <>
      <Helmet>
        <title>CSM | Trivium - Oportunidades de Negócio</title>
      </Helmet>
      
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
        {/* Modern Hero Header with Background Image */}
        <div 
          className="relative overflow-hidden" 
          style={{ 
            backgroundColor: '#1F1746',
            backgroundImage: 'url(/elemento-01.svg)',
            backgroundSize: '200%',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        >
          <div className="absolute inset-0 bg-black/20" />
          <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-white/5" />
          
          <div className="relative container mx-auto px-6 py-6 pb-8">
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                    <Briefcase className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-extrabold text-white">Oportunidades de Negócio</h1>
                    <p className="text-blue-100 text-base pb-2">
                      Gerencie suas oportunidades e acompanhe a evolução dos negócios
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Button 
                  variant="secondary" 
                  size="default" 
                  onClick={handleRetry} 
                  className="backdrop-blur-sm bg-white/20 border-white/30 text-white hover:bg-white/30 transition-all duration-200"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Atualizar
                </Button>
                <Button 
                  size="default" 
                  onClick={handleCreateNew} 
                  className="bg-white text-gray-900 hover:bg-gray-100 shadow-lg"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Nova Oportunidade
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-6 -mt-4 relative z-10 space-y-8">

            {/* Enhanced Stats Cards with Modern Design */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card className="relative overflow-hidden border shadow-sm bg-gradient-to-br from-blue-50 to-white">
                <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-blue-500/10 to-transparent rounded-full transform translate-x-10 -translate-y-10" />
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 bg-blue-100 rounded-md">
                        <Briefcase className="h-4 w-4 text-blue-600" />
                      </div>
                      <span className="text-sm font-extrabold text-gray-600">Total de Oportunidades</span>
                    </div>
                    <div className="text-2xl font-extrabold text-gray-900">
                      {stats.totalOpportunities}
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-gray-500">
                      Oportunidades cadastradas
                    </div>
                    <div className="flex items-center gap-1">
                      <TrendingUp className="h-3 w-3 text-blue-500" />
                      <span className="text-xs text-blue-600 font-extrabold">+12%</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="relative overflow-hidden border shadow-sm bg-gradient-to-br from-green-50 to-white">
                <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-green-500/10 to-transparent rounded-full transform translate-x-10 -translate-y-10" />
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 bg-green-100 rounded-md">
                        <FileText className="h-4 w-4 text-green-600" />
                      </div>
                      <span className="text-sm font-extrabold text-gray-600">Valor Total</span>
                    </div>
                    <div className="text-2xl font-extrabold text-gray-900">
                      {formatCurrency(stats.totalValue, 'BRL')}
                    </div>
                  </div>
                  <div className="text-xs text-gray-500">
                    Valor em pipeline
                  </div>
                </CardContent>
              </Card>

              <Card className="relative overflow-hidden border shadow-sm bg-gradient-to-br from-purple-50 to-white">
                <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-purple-500/10 to-transparent rounded-full transform translate-x-10 -translate-y-10" />
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 bg-purple-100 rounded-md">
                        <BarChart4 className="h-4 w-4 text-purple-600" />
                      </div>
                      <span className="text-sm font-extrabold text-gray-600">Taxa de Conversão</span>
                    </div>
                    <div className="text-2xl font-extrabold text-gray-900">
                      {stats.conversionRate.toFixed(1)}%
                    </div>
                  </div>
                  <div className="text-xs text-gray-500">
                    Oportunidades fechadas
                  </div>
                </CardContent>
              </Card>

              <Card className="relative overflow-hidden border shadow-sm bg-gradient-to-br from-orange-50 to-white">
                <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-orange-500/10 to-transparent rounded-full transform translate-x-10 -translate-y-10" />
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 bg-orange-100 rounded-md">
                        <Target className="h-4 w-4 text-orange-600" />
                      </div>
                      <span className="text-sm font-extrabold text-gray-600">Oportunidades Ganhas</span>
                    </div>
                    <div className="text-2xl font-extrabold text-gray-900">
                      {stats.wonCount}
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-gray-500">
                      {Math.round((stats.wonCount / (stats.totalOpportunities || 1)) * 100)}% do total
                    </div>
                    <div className="flex items-center gap-1">
                      <Star className="h-3 w-3 text-orange-500" />
                      <span className="text-xs text-orange-600 font-extrabold">+8%</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Enhanced Search and Filtering Section */}
            <Card className="border shadow-sm bg-white/80 backdrop-blur-sm">
              <CardHeader className="pb-2">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <h3 className="text-lg font-extrabold">Oportunidades do HubSpot</h3>
                  
                  <div className="flex items-center space-x-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      className={showFilters ? 'bg-muted' : ''}
                      onClick={() => setShowFilters(!showFilters)}
                    >
                      <Filter className="h-4 w-4 mr-1" />
                      <span className="hidden sm:inline">Filtros</span>
                    </Button>
                  </div>
                </div>
              </CardHeader>
            
              {showFilters && (
                <CardContent className="p-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4 py-2">
                    <div className="flex flex-col space-y-1.5">
                      <label className="text-sm font-extrabold">Busca</label>
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input
                          placeholder="Nome ou cliente..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="pl-10 h-10 bg-white border-gray-200 focus:border-blue-500 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                  
                    <div className="flex flex-col space-y-1.5">
                      <label className="text-sm font-extrabold">Status</label>
                      <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger className="h-10 bg-white border-gray-200 focus:border-blue-500 focus:ring-blue-500">
                          <SelectValue placeholder="Selecione o status" />
                        </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        <SelectItem value="open">Abertas</SelectItem>
                        <SelectItem value="negotiation">Em Negociação</SelectItem>
                        <SelectItem value="won">Ganhas</SelectItem>
                        <SelectItem value="lost">Perdidas</SelectItem>
                        <SelectItem value="canceled">Canceladas</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="flex flex-col space-y-1.5">
                      <label className="text-sm font-extrabold">Vendedor</label>
                      <Input
                        placeholder="Nome do vendedor..."
                        value={vendorFilter}
                        onChange={(e) => setVendorFilter(e.target.value)}
                        className="h-10 bg-white border-gray-200 focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                  
                    <div className="flex flex-col space-y-1.5">
                      <label className="text-sm font-extrabold">Período</label>
                      <Select value={timeFilter} onValueChange={setTimeFilter as any}>
                        <SelectTrigger className="h-10 bg-white border-gray-200 focus:border-blue-500 focus:ring-blue-500">
                          <SelectValue placeholder="Selecione o período" />
                        </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todo período</SelectItem>
                        <SelectItem value="week">Última semana</SelectItem>
                        <SelectItem value="month">Último mês</SelectItem>
                        <SelectItem value="quarter">Último trimestre</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="flex flex-col space-y-1.5">
                      <label className="text-sm font-extrabold">Valor</label>
                      <Select value={valueFilter} onValueChange={setValueFilter as any}>
                        <SelectTrigger className="h-10 bg-white border-gray-200 focus:border-blue-500 focus:ring-blue-500">
                          <SelectValue placeholder="Selecione o valor" />
                        </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        <SelectItem value="high">Alto (≥ 100mil)</SelectItem>
                        <SelectItem value="medium">Médio (10mil - 100mil)</SelectItem>
                        <SelectItem value="low">Baixo (menos de 10mil)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between mt-4">
                    <div className="text-sm text-muted-foreground">
                      Exibindo {filteredOpportunities.length} oportunidade(s)
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setSearchQuery('');
                        setStatusFilter('all');
                        setTimeFilter('all');
                        setValueFilter('all');
                        setVendorFilter('');
                      }}
                    >
                      Limpar filtros
                    </Button>
                  </div>
                </CardContent>
              )}
              
              <CardContent className="pt-6">
                <ScrollArea className="w-full">
                  {renderTable()}
                  <ScrollBar orientation="horizontal" />
                </ScrollArea>
                  
                {renderPagination()}
              </CardContent>
            </Card>

            {/* Bottom Spacing */}
            <div className="h-16" />
        </div>
      </div>
    </>
  );
};

export default BusinessOpportunitiesPage;